import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { 
  ArrowLeft,
  HardHat, 
  MapPin, 
  CheckCircle, 
  Calendar,
  Star,
  MessageCircle,
  Upload,
  FileText,
  Send,
  Phone,
  Mail,
  Clock,
  Building2,
  Zap,
  Shield,
  Users
} from 'lucide-react'
import apiService from '../services/api'
import SEO from '../components/SEO'
import { useAuthStore } from '../store/authStore'
import { shouldShowFeature } from '../utils/userPermissions'
import { toast } from 'react-toastify'
import VendorRatings from '../components/VendorRatings'

interface VendorDetail {
  id: string
  companyName: string
  certifications: string[] | null
  firstName: string
  lastName: string
  email: string
  avatar: string | null
  location: {
    city: string
    state: string
  }
  contact: {
  phone: string
  }
  governmentVendor: {
    isGovCertified: boolean
    cageCode: string | null
    dunsNumber: string | null
    gsaScheduleId: string | null
    certifications: string[] | null
    approvedAgencies: string[] | null
  }
  stats: {
    completedProjects: number
    activeListings: number
    memberSince: string
    govContractsCompleted: number
  }
  recentProjects: Array<{
    id: string
    title: string
    description: string
    completedAt: string
  }>
  listings: Array<{
    id: string
    title: string
    description: string
    status: string
  }>
}

interface Message {
  id: string
  content: string
  senderId: string
  receiverId: string
  sentAt: string
  isRead: boolean
}

interface QuoteRequest {
  projectType: string
  systemSizeKw: number
  location: string
  budget: number
  timeline: string
  description: string
  documents: File[]
}

export default function VendorDetailPage() {
  const { vendorId } = useParams<{ vendorId: string }>()
  const { user } = useAuthStore()
  
  const [vendor, setVendor] = useState<VendorDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'overview' | 'projects' | 'documents' | 'contact'>('overview')
  
  // Message state
  const [messages, setMessages] = useState<Message[]>([])
  const [newMessage, setNewMessage] = useState('')
  const [showMessageModal, setShowMessageModal] = useState(false)
  
  // Quote request state
  const [showQuoteModal, setShowQuoteModal] = useState(false)
  const [quoteRequest, setQuoteRequest] = useState<QuoteRequest>({
    projectType: '',
    systemSizeKw: 0,
    location: '',
    budget: 0,
    timeline: '',
    description: '',
    documents: []
  })
  
  // Document upload state
  const [uploadedDocuments, setUploadedDocuments] = useState<File[]>([])

  useEffect(() => {
    if (vendorId) {
      fetchVendorDetails()
    }
  }, [vendorId])

  const fetchVendorDetails = async () => {
    try {
      setLoading(true)
      const response = await apiService.get(`/api/enterprise/vendor/${vendorId}`) as any
      if (response.success) {
        setVendor(response.data)
      } else {
        setError('Failed to fetch vendor details')
      }
    } catch (err) {
      setError('Failed to fetch vendor details')
      console.error('Error fetching vendor details:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !user || !vendor) return

    try {
      console.log('🚀 Sending message from VendorDetailPage:', {
        receiverId: vendor.id,
        content: newMessage.trim(),
        messageType: 'GENERAL'
      })

      const response = await apiService.post('/api/messages', {
        receiverId: vendor.id,
        content: newMessage.trim(),
        messageType: 'GENERAL'
      }) as any

      console.log('✅ Message sent successfully:', response)

      if (response.success) {
        setMessages([...messages, response.data])
        setNewMessage('')
        setShowMessageModal(false)
        
        // Navigate to messages page to see the conversation
        window.location.href = `/messages?user=${vendor.id}`
      }
    } catch (err) {
      console.error('Error sending message:', err)
      alert('Failed to send message. Please try again.')
    }
  }

  const handleQuoteSubmit = async () => {
    console.log('🚀 handleQuoteSubmit called')
    console.log('User:', user)
    console.log('Vendor:', vendor)
    console.log('Quote Request:', quoteRequest)
    
    if (!user || !vendor) {
      console.log('❌ Missing user or vendor data')
      return
    }

    try {
      // First upload documents if any
      const documentUrls: string[] = []
      
      if (uploadedDocuments.length > 0) {
        const file = uploadedDocuments[0];
        console.log('📄 File details:', {
          name: file.name,
          type: file.type,
          size: file.size,
          lastModified: file.lastModified
        });
        
        const formData = new FormData();
        formData.append('file', file); // Only single PDF allowed
        
        console.log('📤 FormData contents:');
        for (let pair of formData.entries()) {
          console.log(pair[0], pair[1]);
        }
        const uploadResponse = await apiService.post('/api/upload/quote-documents', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        }) as any;
        if (uploadResponse.success && uploadResponse.data.documentUrl) {
          documentUrls.push(uploadResponse.data.documentUrl);
        }
      }

      console.log('📤 Submitting quote request to API...')
      // Submit quote request
      const response = await apiService.post('/api/enterprise/quote-request', {
        vendorId: vendor.id,
        ...quoteRequest,
        documentUrls
      }) as any

      console.log('✅ Quote request response:', response)

      if (response.success) {
        toast.success('Quote request submitted successfully!')
        setShowQuoteModal(false)
        setQuoteRequest({
          projectType: '',
          systemSizeKw: 0,
          location: '',
          budget: 0,
          timeline: '',
          description: '',
          documents: []
        })
        setUploadedDocuments([])
      } else {
        console.log('❌ Quote request failed:', response)
        toast.error('Failed to submit quote request: ' + (response.message || 'Unknown error'))
      }
    } catch (err) {
      console.error('❌ Error submitting quote request:', err)
      alert('An error occurred while submitting the quote request. Please try again.')
    }
  }

  const handleDocumentUpload = (files: FileList | null) => {
    if (files) {
      const newFiles = Array.from(files)
      setUploadedDocuments([...uploadedDocuments, ...newFiles])
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading vendor details...</p>
        </div>
      </div>
    )
  }

  if (error || !vendor) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'Vendor not found'}</p>
          <Link 
            to="/enterprise"
            className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700"
          >
            Back to Directory
          </Link>
        </div>
      </div>
    )
  }

  const vendorLocation = `${vendor.location.city}, ${vendor.location.state}`
  const vendorDescription = `${vendor.companyName} - Certified solar equipment vendor in ${vendorLocation}. ${vendor.governmentVendor.isGovCertified ? 'Government-certified. ' : ''}${vendor.stats.completedProjects} completed projects. Request quotes for solar panels, inverters, and equipment.`

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title={`${vendor.companyName} - Solar Equipment Vendor`}
        description={vendorDescription}
        keywords={`${vendor.companyName}, solar vendor, solar equipment supplier, ${vendorLocation}, solar panels, solar inverters, government solar contractor`}
        image={vendor.avatar || undefined}
        url={typeof window !== 'undefined' ? window.location.href : `https://evoltbay.com/enterprise/vendor/${vendorId}`}
      />
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center space-x-4 mb-6">
            <Link 
              to="/enterprise"
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back to Directory
            </Link>
          </div>

          {/* Vendor Header */}
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between">
            <div className="flex items-start space-x-6 mb-6 lg:mb-0">
              <div className="w-24 h-24 bg-primary-100 rounded-xl flex items-center justify-center flex-shrink-0">
                {vendor.avatar ? (
                  <img
                    src={vendor.avatar}
                    alt={vendor.companyName}
                    className="w-24 h-24 rounded-xl object-cover"
                  />
                ) : (
                  <HardHat className="h-12 w-12 text-primary-600" />
                )}
              </div>
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  {vendor.companyName}
                </h1>
                <div className="flex items-center text-gray-600 mb-3">
                  <MapPin className="h-5 w-5 mr-2" />
                  <span>{vendor.location.city}, {vendor.location.state}</span>
                </div>
                <div className="flex items-center space-x-6 text-sm text-gray-600">
                  <div className="flex items-center">
                    <CheckCircle className="h-4 w-4 text-green-500 mr-1" />
                    <span>{vendor.stats.completedProjects} completed jobs</span>
                  </div>
                  <div className="flex items-center">
                    <Calendar className="h-4 w-4 mr-1" />
                    <span>Member since {formatDate(vendor.stats.memberSince)}</span>
                  </div>
                  <div className="flex items-center">
                    <Star className="h-4 w-4 text-yellow-500 mr-1" />
                    <span>4.8 rating</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            {user && (
              <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
                {shouldShowFeature(user, 'canRequestQuote') ? (
                  <>
                    <button
                      onClick={() => setShowMessageModal(true)}
                      className="flex items-center justify-center px-6 py-3 border border-primary-600 text-primary-600 rounded-lg hover:bg-primary-50 transition-colors"
                    >
                      <MessageCircle className="h-5 w-5 mr-2" />
                      Send Message
                    </button>
                    <button
                      onClick={() => {
                        console.log('🔘 Request Quote button clicked')
                        setShowQuoteModal(true)
                      }}
                      className="flex items-center justify-center px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                    >
                      <FileText className="h-5 w-5 mr-2" />
                      Request Quote
                    </button>
                  </>
                ) : (
                  <div className="w-full bg-gray-100 border border-gray-300 rounded-lg px-6 py-4 text-center">
                    <div className="flex items-center justify-center space-x-2 text-gray-500 mb-1">
                      <HardHat className="h-5 w-5" />
                      <span className="font-medium">Account Approval Required</span>
                    </div>
                    <p className="text-sm text-gray-400">
                      Contact admin to send messages and request quotes
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {[
              { id: 'overview', label: 'Overview', icon: Building2 },
              { id: 'projects', label: 'Projects', icon: Zap },
              { id: 'documents', label: 'Documents', icon: FileText },
              { id: 'contact', label: 'Contact', icon: Phone }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center px-1 py-4 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="h-4 w-4 mr-2" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* About */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">About</h2>
                <p className="text-gray-600 leading-relaxed">
                  {vendor.companyName} is a verified solar enterprise vendor with {vendor.stats.completedProjects} completed projects. 
                  {vendor.governmentVendor.isGovCertified && ' This vendor is certified for government contracts.'}
                </p>
              </div>

              {/* Vendor Ratings & Reviews */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Customer Reviews</h2>
                <VendorRatings vendorId={vendor.id} showReviews={true} maxReviews={3} />
              </div>

              {/* Government Certifications */}
              {vendor.governmentVendor.isGovCertified && (
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Government Certifications</h2>
                  <div className="space-y-3">
                    {vendor.governmentVendor.cageCode && (
                      <div className="flex items-center">
                        <Shield className="h-4 w-4 text-green-500 mr-2" />
                        <span className="text-gray-600 text-sm">CAGE Code: {vendor.governmentVendor.cageCode}</span>
                      </div>
                    )}
                    {vendor.governmentVendor.dunsNumber && (
                      <div className="flex items-center">
                        <Shield className="h-4 w-4 text-green-500 mr-2" />
                        <span className="text-gray-600 text-sm">DUNS Number: {vendor.governmentVendor.dunsNumber}</span>
                      </div>
                    )}
                    {vendor.governmentVendor.gsaScheduleId && (
                      <div className="flex items-center">
                        <Shield className="h-4 w-4 text-green-500 mr-2" />
                        <span className="text-gray-600 text-sm">GSA Schedule: {vendor.governmentVendor.gsaScheduleId}</span>
                      </div>
                    )}
                    {(vendor.governmentVendor.certifications || []).map((cert, index) => (
                      <div key={index} className="flex items-center">
                        <Shield className="h-4 w-4 text-green-500 mr-2" />
                        <span className="text-gray-600 text-sm">{cert}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Specialties */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Service Areas</h2>
                <div className="flex flex-wrap gap-2">
                  {(vendor.governmentVendor.approvedAgencies || []).map((agency, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-primary-100 text-primary-800 rounded-full text-sm font-medium"
                    >
                      {agency}
                    </span>
                  ))}
                  {(!vendor.governmentVendor.approvedAgencies || vendor.governmentVendor.approvedAgencies.length === 0) && (
                    <span className="text-gray-500 text-sm">Solar Installation, Commercial Solar, Residential Solar</span>
                  )}
                </div>
              </div>

              {/* Recent Projects */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Recent Projects</h2>
                <div className="space-y-4">
                  {(vendor.recentProjects || []).slice(0, 3).map((project) => (
                    <div key={project.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-medium text-gray-900">{project.title}</h3>
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          completed
                        </span>
                      </div>
                      <p className="text-gray-600 text-sm mb-2">{project.description}</p>
                      <div className="flex items-center space-x-4 text-xs text-gray-500">
                        <span>Solar Installation</span>
                        <span>{formatDate(project.completedAt)}</span>
                      </div>
                    </div>
                  ))}
                  {(!vendor.recentProjects || vendor.recentProjects.length === 0) && (
                    <div className="text-center py-8">
                      <p className="text-gray-500">No recent projects available</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Quick Stats */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Stats</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                      <span className="text-gray-600">Projects Completed</span>
                    </div>
                    <span className="font-semibold text-gray-900">{vendor.stats.completedProjects}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Users className="h-5 w-5 text-blue-500 mr-2" />
                      <span className="text-gray-600">Active Listings</span>
                    </div>
                    <span className="font-semibold text-gray-900">{vendor.stats.activeListings}</span>
                  </div>
                  {vendor.governmentVendor.isGovCertified && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                        <Shield className="h-5 w-5 text-green-500 mr-2" />
                        <span className="text-gray-600">Gov Contracts</span>
                    </div>
                      <span className="font-semibold text-gray-900">{vendor.stats.govContractsCompleted}</span>
                  </div>
                  )}
                </div>
              </div>

              {/* Certifications */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Certifications</h3>
                <div className="space-y-3">
                  {(vendor.certifications || []).map((cert, index) => (
                    <div key={index} className="flex items-center">
                      <Shield className="h-4 w-4 text-green-500 mr-2" />
                      <span className="text-gray-600 text-sm">{cert}</span>
                    </div>
                  ))}
                  {(!vendor.certifications || vendor.certifications.length === 0) && (
                    <p className="text-gray-500 text-sm">No certifications listed</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'projects' && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">All Projects</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {(vendor.recentProjects || []).map((project) => (
                <div key={project.id} className="border border-gray-200 rounded-lg p-6">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-semibold text-gray-900">{project.title}</h3>
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      completed
                    </span>
                  </div>
                  <p className="text-gray-600 mb-4">{project.description}</p>
                  <div className="space-y-2 text-sm text-gray-500">
                    <div className="flex items-center">
                      <Zap className="h-4 w-4 mr-2" />
                      <span>Solar Installation</span>
                    </div>
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-2" />
                      <span>Completed {formatDate(project.completedAt)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {(!vendor.recentProjects || vendor.recentProjects.length === 0) && (
              <div className="text-center py-12">
                <p className="text-gray-500">No projects available</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'documents' && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Available Listings</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {(vendor.listings || []).map((listing) => (
                <div key={listing.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center">
                      <FileText className="h-5 w-5 text-gray-400 mr-2" />
                      <span className="font-medium text-gray-900 truncate">{listing.title}</span>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      listing.status === 'active' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {listing.status}
                    </span>
                  </div>
                  <div className="text-sm text-gray-500">
                    <p>{listing.description}</p>
                  </div>
                </div>
              ))}
            </div>
            {(!vendor.listings || vendor.listings.length === 0) && (
              <div className="text-center py-12">
                <p className="text-gray-500">No listings available</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'contact' && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Contact Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <div className="flex items-center">
                  <Mail className="h-5 w-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600">Email</p>
                    <p className="font-medium text-gray-900">{vendor.email}</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <Phone className="h-5 w-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600">Phone</p>
                    <p className="font-medium text-gray-900">{vendor.contact.phone}</p>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex items-center">
                  <MapPin className="h-5 w-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600">Location</p>
                    <p className="font-medium text-gray-900">{vendor.location.city}, {vendor.location.state}</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <Clock className="h-5 w-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600">Business Hours</p>
                    <p className="font-medium text-gray-900">Mon-Fri 8:00 AM - 6:00 PM</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Message Modal */}
      {showMessageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Send Message to {vendor.companyName}
            </h3>
            <textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type your message here..."
              className="w-full h-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
            />
            <div className="flex justify-end space-x-3 mt-4">
              <button
                onClick={() => setShowMessageModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleSendMessage}
                disabled={!newMessage.trim()}
                className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="h-4 w-4 mr-2" />
                Send Message
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quote Request Modal */}
      {showQuoteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6 my-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">
              Request Quote from {vendor.companyName}
            </h3>
            
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Project Type
                  </label>
                  <select
                    value={quoteRequest.projectType}
                    onChange={(e) => setQuoteRequest({...quoteRequest, projectType: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">Select project type</option>
                    <option value="ROOFTOP">Rooftop</option>
                    <option value="GROUND">Ground Mount</option>
                    <option value="UTILITY_SCALE">Utility Scale</option>
                    <option value="COMMERCIAL">Commercial</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    System Size (kW)
                  </label>
                  <input
                    type="number"
                    value={quoteRequest.systemSizeKw || ''}
                    onChange={(e) => setQuoteRequest({...quoteRequest, systemSizeKw: Number(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="e.g., 100"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Location
                  </label>
                  <input
                    type="text"
                    value={quoteRequest.location}
                    onChange={(e) => setQuoteRequest({...quoteRequest, location: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="City, State or ZIP code"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Budget Range ($)
                  </label>
                  <input
                    type="number"
                    value={quoteRequest.budget || ''}
                    onChange={(e) => setQuoteRequest({...quoteRequest, budget: Number(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="e.g., 500000"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Timeline
                </label>
                <select
                  value={quoteRequest.timeline}
                  onChange={(e) => setQuoteRequest({...quoteRequest, timeline: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Select timeline</option>
                  <option value="ASAP">ASAP</option>
                  <option value="1-3 months">1-3 months</option>
                  <option value="3-6 months">3-6 months</option>
                  <option value="6-12 months">6-12 months</option>
                  <option value="12+ months">12+ months</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Project Description
                </label>
                <textarea
                  value={quoteRequest.description}
                  onChange={(e) => setQuoteRequest({...quoteRequest, description: e.target.value})}
                  placeholder="Describe your project requirements, special considerations, etc."
                  className="w-full h-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload Documents
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                  <input
                    type="file"
                    multiple
                    onChange={(e) => handleDocumentUpload(e.target.files)}
                    className="hidden"
                    id="document-upload"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                  />
                  <label
                    htmlFor="document-upload"
                    className="flex flex-col items-center justify-center cursor-pointer"
                  >
                    <Upload className="h-8 w-8 text-gray-400 mb-2" />
                    <span className="text-sm text-gray-600">
                      Click to upload or drag and drop
                    </span>
                    <span className="text-xs text-gray-500 mt-1">
                      PDF, DOC, XLS, PNG, JPG up to 10MB each
                    </span>
                  </label>
                </div>
                
                {uploadedDocuments.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {uploadedDocuments.map((file, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                        <span className="text-sm text-gray-700">{file.name}</span>
                        <button
                          onClick={() => setUploadedDocuments(uploadedDocuments.filter((_, i) => i !== index))}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowQuoteModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  console.log('📝 Submit Quote Request button clicked')
                  handleQuoteSubmit()
                }}
                disabled={!quoteRequest.projectType || !quoteRequest.systemSizeKw || !quoteRequest.location}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Submit Quote Request
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
} 